import React, { useState, useEffect } from 'react';
import { Users, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

const Step0_AccountSelection = ({ wizardData, updateData, setWizardData }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(wizardData.selectedAccount || null);
  const [existingDataLoaded, setExistingDataLoaded] = useState(false);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch all family members (potential test accounts)
      const { data: familyMembers, error: familyError } = await supabase
        .from('family_members')
        .select(`
          id,
          name,
          email,
          role,
          auth_user_id,
          created_at
        `)
        .order('created_at', { ascending: false });

      if (familyError) throw familyError;

      setAccounts(familyMembers || []);
    } catch (err) {
      console.error('Error fetching accounts:', err);
      setError('Failed to load accounts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAccount = async (account) => {
    setSelectedAccount(account);
    setExistingDataLoaded(false); // Reset flag
    console.log('✅ Selected test account:', {
      id: account.id,
      name: account.name,
      email: account.email
    });

    // Try to load existing profile data for this account
    try {
      const userId = account.auth_user_id || account.id;
      const { data: existingProfile, error } = await supabase
        .from('student_academic_profiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (existingProfile && !error) {
        console.log('📊 Found existing profile data, loading into wizard:', existingProfile);
        setExistingDataLoaded(true); // Mark that we loaded existing data

        // Prepare all the data we need to load
        const profileData = {
          name: existingProfile.name || account.name,
          graduation_year: existingProfile.graduation_year,
          grade_level: existingProfile.grade_level,
          state_of_residency: existingProfile.state_of_residency,
          intended_major: existingProfile.intended_major,
          intended_minor: existingProfile.intended_minor,
          gpa_weighted: existingProfile.gpa_weighted,
          gpa_unweighted: existingProfile.gpa_unweighted,
          gpa_scale: existingProfile.gpa_scale,
          class_rank: existingProfile.class_rank,
          class_size: existingProfile.class_size,
          sat_total: existingProfile.sat_total,
          sat_math: existingProfile.sat_math,
          sat_reading_writing: existingProfile.sat_reading_writing,
          act_composite: existingProfile.act_composite,
          act_math: existingProfile.act_math,
          act_english: existingProfile.act_english,
          act_reading: existingProfile.act_reading,
          act_science: existingProfile.act_science,
          curriculum_rigor: existingProfile.curriculum_rigor,
          ap_courses_taken: existingProfile.ap_courses_taken,
          honors_courses: existingProfile.honors_courses,
          dual_enrollment_credits: existingProfile.dual_enrollment_credits,
          career_goals: existingProfile.career_goals,
          special_talents: existingProfile.special_talents,
          high_school_name: existingProfile.high_school_name,
          high_school_city: existingProfile.high_school_city,
          high_school_state: existingProfile.high_school_state,
          extracurriculars: existingProfile.extracurriculars || [],
          honors: existingProfile.awards || []
        };

        // Load parent insights if available
        const parentInsights = {
          parentA: {
            easy_subject: existingProfile.parent_observation_easy_subject,
            growth: existingProfile.parent_observation_growth,
            learning_difference: existingProfile.parent_observation_learning_difference,
            surprises: existingProfile.parent_observation_surprises,
            age_limit: existingProfile.parent_observation_age_limit,
            rules_questioned: existingProfile.parent_observation_rules_questioned,
            wrong_level: existingProfile.parent_observation_wrong_level,
            advocacy_success: existingProfile.parent_observation_advocacy_success,
            initiative_against_advice: existingProfile.parent_observation_initiative_against_advice,
            mechanical_skills: existingProfile.parent_observation_mechanical_skills,
            math_integration: existingProfile.parent_observation_math_integration,
            entrepreneurial_instincts: existingProfile.parent_observation_entrepreneurial_instincts,
            obsessions: existingProfile.parent_observation_obsessions,
            unusual_interests: existingProfile.parent_observation_unusual_interests,
            hidden_qualities: existingProfile.parent_observation_hidden_qualities
          },
          parentB: {
            easy_subject: existingProfile.parent_b_easy_subject,
            growth: existingProfile.parent_b_growth,
            learning_difference: existingProfile.parent_b_learning_difference,
            surprises: existingProfile.parent_b_surprises,
            age_limit: existingProfile.parent_b_age_limit,
            rules_questioned: existingProfile.parent_b_rules_questioned,
            wrong_level: existingProfile.parent_b_wrong_level,
            advocacy_success: existingProfile.parent_b_advocacy_success,
            initiative_against_advice: existingProfile.parent_b_initiative_against_advice,
            mechanical_skills: existingProfile.parent_b_mechanical_skills,
            math_integration: existingProfile.parent_b_math_integration,
            entrepreneurial_instincts: existingProfile.parent_b_entrepreneurial_instincts,
            obsessions: existingProfile.parent_b_obsessions,
            unusual_interests: existingProfile.parent_b_unusual_interests,
            hidden_qualities: existingProfile.parent_b_hidden_qualities,
            different_perspective: existingProfile.parent_b_different_perspective,
            what_other_parent_misses: existingProfile.parent_b_what_other_parent_misses
          }
        };

        // Load assessment responses if available
        const { data: assessmentData, error: assessmentError } = await supabase
          .from('assessment_responses')
          .select('responses, initial_questions, dynamic_questions, insights')
          .eq('user_id', userId)
          .single();

        console.log('🔍 Assessment data from database:', {
          assessmentData,
          assessmentError,
          hasInitialQuestions: !!assessmentData?.initial_questions,
          initialQuestionsLength: assessmentData?.initial_questions?.length || 0
        });

        // Prepare all data to load at once
        const dataToLoad = {
          profile: profileData,
          selectedAccount: account
        };

        // Add parent insights if available
        if (Object.values(parentInsights.parentA).some(v => v)) {
          dataToLoad.parentInsights = parentInsights;
        }

        // Add strategic context if available
        if (existingProfile.strategic_context_summary) {
          dataToLoad.strategicContext = {
            summary: existingProfile.strategic_context_summary
          };
        }

        // Add assessment data if available
        if (assessmentData && !assessmentError) {
          if (assessmentData.initial_questions) {
            console.log('✅ Loading questionSet:', assessmentData.initial_questions);
            // Migrate old format questions to new format if needed
            const questionSet = assessmentData.initial_questions.map((q, index) => {
              // If already in new format, return as is
              if (q.id && q.type && q.placeholder !== undefined) {
                return q;
              }
              // Convert old format to new format
              return {
                id: `q${index + 1}`,
                type: 'text',
                focus: '',
                question: q.question,
                placeholder: 'Share your thoughts...',
                rationale: q.rationale
              };
            });
            dataToLoad.questionSet = questionSet;
          }
          if (assessmentData.dynamic_questions) {
            console.log('✅ Loading followupQuestions:', assessmentData.dynamic_questions);
            // Migrate old format questions to new format if needed
            const followupQuestions = assessmentData.dynamic_questions.map((q, index) => {
              // If already in new format, return as is
              if (q.id && q.type && q.placeholder !== undefined) {
                return q;
              }
              // Convert old format to new format
              return {
                id: `q${index + 9}`,
                type: 'text',
                focus: '',
                question: q.question,
                placeholder: 'Share your thoughts...',
                rationale: q.rationale
              };
            });
            dataToLoad.followupQuestions = followupQuestions;
          }
          if (assessmentData.insights) {
            console.log('✅ Loading finalAssessment');
            dataToLoad.finalAssessment = assessmentData.insights;
          }

          if (assessmentData.responses) {
            // Extract initial answers (questions 1-8)
            const initialAnswers = {};
            for (let i = 1; i <= 8; i++) {
              if (assessmentData.responses[`q${i}`]) {
                initialAnswers[`q${i}`] = assessmentData.responses[`q${i}`];
              }
            }

            // Extract followup answers (questions 9-15)
            const followupAnswers = {};
            for (let i = 9; i <= 15; i++) {
              if (assessmentData.responses[`q${i}`]) {
                followupAnswers[`q${i}`] = assessmentData.responses[`q${i}`];
              }
            }

            if (Object.keys(initialAnswers).length > 0) {
              dataToLoad.initialAnswers = initialAnswers;
            }
            if (Object.keys(followupAnswers).length > 0) {
              dataToLoad.followupAnswers = followupAnswers;
            }
          }
        }

        // Load all data at once using setWizardData from parent
        console.log('📦 Loading all data at once:', dataToLoad);

        // Update ALL data at once to avoid race conditions with localStorage
        setWizardData(prev => ({
          ...prev,
          ...dataToLoad
        }));

        console.log('✅ Loaded existing profile data into wizard');
      } else {
        console.log('ℹ️ No existing profile data found for this account');
      }
    } catch (error) {
      console.error('Error loading existing profile:', error);
      // Don't throw - just continue with empty wizard
      // Still update selectedAccount even if loading failed
      updateData('selectedAccount', account);
    }
  };

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-slate-900 mb-2">Select Test Account</h2>
        <p className="text-slate-600">
          Choose the account you want to populate with test data. All wizard data will be saved to this account's student profile.
        </p>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader className="w-8 h-8 text-azure-600 animate-spin" />
          <span className="ml-3 text-slate-600">Loading accounts...</span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-semibold text-red-900 mb-1">Error Loading Accounts</h4>
            <p className="text-sm text-red-700">{error}</p>
            <button
              onClick={fetchAccounts}
              className="mt-3 text-sm text-red-700 hover:text-red-900 font-medium"
            >
              Try Again
            </button>
          </div>
        </div>
      )}

      {!loading && !error && accounts.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-yellow-600 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-yellow-900 mb-2">No Accounts Found</h3>
          <p className="text-sm text-yellow-700">
            No test accounts are available. Create a test account first, then return to this wizard.
          </p>
        </div>
      )}

      {!loading && !error && accounts.length > 0 && (
        <div className="space-y-4">
          <div className="bg-azure-50 border border-azure-200 rounded-lg p-4">
            <h3 className="font-semibold text-azure-900 mb-2">Available Accounts</h3>
            <p className="text-sm text-azure-700">
              Select the account you want to use for testing. You can populate any account with test data.
            </p>
          </div>

          <div className="grid gap-3">
            {accounts.map((account) => {
              const isSelected = selectedAccount?.id === account.id;
              const displayName = account.name || 'Unnamed Account';
              const accountEmail = account.email || 'No email';

              return (
                <button
                  key={account.id}
                  onClick={() => handleSelectAccount(account)}
                  className={`
                    w-full text-left p-4 rounded-lg border-2 transition-all
                    ${isSelected
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-slate-200 bg-white hover:border-azure-300 hover:bg-azure-50'
                    }
                  `}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`
                        w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0
                        ${isSelected ? 'bg-emerald-600' : 'bg-slate-200'}
                      `}>
                        {isSelected ? (
                          <CheckCircle className="w-6 h-6 text-white" />
                        ) : (
                          <Users className="w-6 h-6 text-slate-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h4 className={`font-semibold ${isSelected ? 'text-emerald-900' : 'text-slate-900'}`}>
                          {displayName}
                        </h4>
                        <p className={`text-sm ${isSelected ? 'text-emerald-700' : 'text-slate-600'}`}>
                          {accountEmail}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-xs">
                          <span className={`px-2 py-1 rounded-full ${
                            isSelected ? 'bg-emerald-200 text-emerald-800' : 'bg-slate-200 text-slate-700'
                          }`}>
                            {account.role || 'No role'}
                          </span>
                          <span className={isSelected ? 'text-emerald-600' : 'text-slate-500'}>
                            ID: {account.id.substring(0, 8)}...
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {selectedAccount && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-emerald-900 mb-1">
                  {existingDataLoaded ? 'Existing Profile Loaded!' : 'Account Selected!'}
                </h4>
                <p className="text-sm text-emerald-700">
                  {existingDataLoaded ? (
                    <>
                      Loaded existing profile data for <strong>{selectedAccount.name}</strong>.
                      You can regenerate any step or make changes as needed.
                    </>
                  ) : (
                    <>
                      All wizard data will be saved to <strong>{selectedAccount.name}</strong>'s profile.
                    </>
                  )}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {!selectedAccount && accounts.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            ⚠️ Please select an account before proceeding to the next step.
          </p>
        </div>
      )}
    </div>
  );
};

export default Step0_AccountSelection;
